import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputBase,
  Avatar,
  Badge,
  styled,
  useTheme,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  useMediaQuery,
  Card,
  CardContent,
  Popover,
  Chip,
  Stack,
  Collapse,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Help as HelpIcon,
  Search as SearchIcon,
  Message as MessageIcon,
  Web as WebIcon,
  Email as EmailIcon,
  Sensors as SensorsIcon,
  Router as RouterIcon,
  Groups,
} from "@mui/icons-material";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import SearchBar from "./SearchBar";
import logo from "../assets/Soyl Logo_with_Text.png";
import NotificationMenu from "./NotificationMenu";
import { ChevronDown } from "react-feather";

const drawerWidth = 240; // Width of the sidebar in pixels

// Styled Main component to handle both overlay and persistent modes
const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})(({ theme, open, isMobile }) => ({
  flexGrow: 1, // Takes available space
  padding: 24, // Inner spacing
  transition: theme.transitions.create("margin", {
    // Smooth animation
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // For mobile: don't shift content when drawer is open
  // For desktop: shift content when drawer is open
  marginLeft: isMobile ? 0 : `-${drawerWidth}px`, // Shift content on desktop when closed
  ...(open &&
    !isMobile && {
    // When open on desktop
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0, // No shift when open
  }),
}));

// Styled Search bar
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: 25,
  backgroundColor: theme.palette.grey[100],
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
  },
  display: "flex",
  alignItems: "center",
  height: 50,
  width: 300,
  [theme.breakpoints.down("sm")]: {
    // Responsive for small screens
    width: "100%",
    maxWidth: 200,
  },
}));

// Styled Search Icon Wrapper
const SearchIconWrapper = styled("div")(({ theme }) => ({
  height: 36,
  width: 46,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: "#f5c518",
  marginLeft: theme.spacing(1),
}));

// Styled Input for Search
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  fontSize: 14,
  width: "100%",
  height: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 2),
    width: "100%",
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: "1.5rem",
  letterSpacing: 1,
  marginLeft: theme.spacing(1),
}));

// Logo component (unchanged)
const Logo = () => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box
        component="img"
        src={logo}
        alt="Soyl Logo"
        sx={{
          width: 150,
          height: 80,
        }}
      />
      {/* <LogoText>
        <span style={{ color: "#f0b400" }}>S</span>
        <span style={{ color: "#00a86b" }}>o</span>
        <span style={{ color: "#007fff" }}>y</span>
        <span style={{ color: "#00a86b" }}>l</span>
      </LogoText> */}
    </Box>
  );
};

const SideBar = () => {
  const theme = useTheme(); // Access MUI theme
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // Check if screen is mobile
  const [open, setOpen] = useState(!isMobile); // Drawer open by default on desktop
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar for logout feedback
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false); // Logout dialog
  const navigate = useNavigate(); // For navigation
  const { accessToken, setAccessToken, setRefreshToken, setIsAuthenticated } =
    useAuth(); // Auth context

  const baseURL = process.env.REACT_APP_BASEURL; // Backend API base URL

  const location = useLocation();
  const mainSegment = location.pathname.split("/")[2] || "";

  // States for user data
  const [userData, setUser] = useState(null); // User details
  const [sites, setSites] = useState(null); // Site data
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state

  // Profile card popover state
  const [anchorEl, setAnchorEl] = useState(null); // Popover anchor
  const profileOpen = Boolean(anchorEl); // Popover visibility

  const [sitesOpen, setSitesOpen] = useState(false);

  // Fetch user data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Step 1: Fetch all sites
        const sitesRes = await axios.get(`${baseURL}/get-sites`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const fetchedSites = sitesRes.data.data || [];
        setSites(fetchedSites);

        // Step 2: Fetch user details
        const userRes = await axios.get(`${baseURL}/users`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userData = userRes.data[0]; // Extract first user from array
        setUser(userData);
      } catch (error) {
        console.error(
          "Error fetching data:",
          error.response ? error.response.data : error.message
        );
        setError(
          "Failed to load account or site data. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accessToken]);

  // Update drawer state when screen size changes
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const path = location.pathname;

    if (
      path.startsWith("/dashboard/sites") ||
      path.startsWith("/sites/") ||
      path.startsWith("/water-balance")
    ) {
      setSitesOpen(true);
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  // Open confirmation dialog
  const handleLogoutClick = () => {
    setConfirmLogoutOpen(true);
  };

  // Confirm logout and perform the action
  const handleConfirmLogout = () => {
    setConfirmLogoutOpen(false);
    setAccessToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setSnackbarOpen(true);
    setTimeout(() => navigate("/"), 1000);
  };

  // Cancel logout
  const handleCancelLogout = () => {
    setConfirmLogoutOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Profile card handlers
  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard",
      segment: "",
    },
    {
      text: "Sites",
      icon: <WebIcon />,
      path: "/dashboard/sites",
      segment: "sites",
    },
    {
      text: "Groups",
      icon: <Groups />,
      path: "/dashboard/groups",
      segment: "groups",
    },
    {
      text: "Devices",
      icon: <RouterIcon />,
      path: "/dashboard/devices",
      segment: "devices",
    },
    {
      text: "Receivers",
      icon: <SensorsIcon />,
      path: "/dashboard/receivers",
      segment: "receivers",
    },
    {
      text: "Statistics",
      icon: <BarChartIcon />,
      path: "/dashboard/statistics",
      segment: "statistics",
    },
    {
      text: "Report",
      icon: <DescriptionIcon />,
      path: "/dashboard/report",
      segment: "report",
    },
    {
      text: "Account",
      icon: <PersonIcon />,
      path: "/dashboard/account",
      segment: "account",
    },
    {
      text: "Notification",
      icon: <NotificationsIcon />,
      path: "/dashboard/notification",
      segment: "notification",
    },
    {
      text: "Settings",
      icon: <SettingsIcon />,
      path: "/dashboard/settings",
      segment: "settings",
    },
    { text: "Logout", icon: <LogoutIcon />, onClick: handleLogoutClick },
  ];

  // Handle navigation for menu items
  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
      // Close drawer automatically on mobile after navigation
      if (isMobile) {
        setOpen(false);
      }
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userData) return "";
    return `${userData.first_name ? userData.first_name.charAt(0) : ""}${userData.last_name ? userData.last_name.charAt(0) : ""
      }`;
  };

  // Get full name
  const getFullName = () => {
    if (!userData) return "Loading...";
    return (
      `${userData.first_name || ""} ${userData.last_name || ""}`.trim() ||
      userData.username
    );
  };

  // Drawer content component to avoid repetition
  const drawerContent = (
    <>
      {/* Logo and toggle button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Logo />
        <IconButton onClick={handleDrawerToggle} sx={{ color: "white" }}>
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      <Divider sx={{ bgcolor: "rgba(233, 212, 212, 0.1)" }} />

      {/* Navigation items */}
      <List>
        {menuItems.map((item) => {
          // Normal menu items
          if (item.text !== "Sites") {
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() =>
                    item.onClick ? item.onClick() : handleNavigation(item.path)
                  }
                  selected={
                    item.text === "Dashboard"
                      ? location.pathname === "/dashboard"
                      : mainSegment === item.segment
                  }
                  sx={{
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "#fff",
                      fontWeight: "bold",
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: "#444444",
                    },
                    "&:hover": {
                      bgcolor: "primary.main",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            );
          }

          // ---------- SITES DROPDOWN ----------
          return (
            <React.Fragment key="Sites">
              <ListItem disablePadding>
                <ListItemButton onClick={() => setSitesOpen(!sitesOpen)}>
                  <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
                    <WebIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sites" />
                  {sitesOpen ? <ChevronLeftIcon /> : <ChevronDown />}
                </ListItemButton>
              </ListItem>

              <Collapse in={sitesOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 4 }}>
                  {/* View Sites */}
                  <ListItemButton
                    selected={location.pathname === "/dashboard/sites"}
                    onClick={() => handleNavigation("/dashboard/sites")}
                    sx={{
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "#fff",
                        fontWeight: "bold",
                      },
                      "&.Mui-selected:hover": {
                        bgcolor: "#444444",
                      },
                      "&:hover": {
                        bgcolor: "primary.main",
                      },
                    }}
                  >
                    <ListItemText primary="View Sites" />
                  </ListItemButton>

                  {/* Add Site */}
                  {(userData?.is_admin || userData?.is_superuser) && (
                    <ListItemButton
                      selected={location.pathname === "/sites/add"}
                      onClick={() => handleNavigation("/sites/add")}
                      sx={{
                        "&.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "#fff",
                          fontWeight: "bold",
                        },
                        "&.Mui-selected:hover": {
                          bgcolor: "#444444",
                        },
                        "&:hover": {
                          bgcolor: "primary.main",
                        },
                      }}
                    >
                      <ListItemText primary="Add Site" />
                    </ListItemButton>
                  )}

                  {/* Add Water Saved -- ONLY ADMIN / SUPERUSER */}
                  {(userData?.is_admin || userData?.is_superuser) && (
                    <ListItemButton
                      selected={location.pathname.startsWith(
                        "/water-balance/add"
                      )}
                      onClick={() => handleNavigation("/water-balance/add")}
                      sx={{
                        "&.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "#fff",
                          fontWeight: "bold",
                        },
                        "&.Mui-selected:hover": {
                          bgcolor: "#444444",
                        },
                        "&:hover": {
                          bgcolor: "primary.main",
                        },
                      }}
                    >
                      <ListItemText primary="Add Water Saved" />
                    </ListItemButton>
                  )}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* Help section at bottom */}
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              navigate("/dashboard/help");
              if (isMobile) setOpen(false);
            }}
          >
            <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
              <HelpIcon />
            </ListItemIcon>
            <ListItemText primary="Help" />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Sidebar - Different drawer types based on screen size */}
      {isMobile ? (
        // Temporary drawer (overlay) for mobile
        <Drawer
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              backgroundColor: "#212121",
              color: "white",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        // Persistent drawer for desktop
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              backgroundColor: "#000000",
              color: "white",
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content area with topbar */}
      <Main
        component="main"
        open={open}
        isMobile={isMobile}
        sx={{ flexGrow: 1 }}
      >
        <AppBar
          position="fixed"
          elevation={1}
          sx={{
            width: !isMobile && open ? `calc(100% - ${drawerWidth}px)` : "100%",
            ml: !isMobile && open ? `${drawerWidth}px` : 0,
            backgroundColor: "white",
            color: "text.primary",
            transition: theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Toolbar sx={{ display: "flex", alignItems: "center" }}>
            {/* Always show menu icon on mobile, show only when drawer is closed on desktop */}
            {(isMobile || !open) && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                edge="start"
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Responsive search box */}
            <Box>
              <SearchBar setOpen={setOpen} />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Notification icons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 2 },
              }}
            >
              <IconButton size="large">
                <Badge badgeContent={0} color="error">
                  <MessageIcon />
                </Badge>
              </IconButton>
              <NotificationMenu />

              {/* User profile */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  alignContent: "center",
                  ml: { xs: 1, sm: 2 },
                }}
              >
                {/* Box with Typography hidden on mobile (xs) and shown on md and up */}
                <Box sx={{ mr: 0, display: { xs: "none", md: "block" } }}>
                  {loading ? (
                    <Typography variant="subtitle1">Loading...</Typography>
                  ) : error ? (
                    <Typography variant="subtitle1" color="error">
                      Error loading user
                    </Typography>
                  ) : null}
                </Box>
                {/* Avatar always visible and clickable */}
                <IconButton onClick={handleProfileClick}>
                  <Avatar sx={{ bgcolor: theme.palette.warning.light }}>
                    {getUserInitials()}
                  </Avatar>
                </IconButton>

                {/* Profile Card Popover */}
                <Popover
                  open={profileOpen}
                  anchorEl={anchorEl}
                  onClose={handleProfileClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <Card sx={{ width: "100%", p: 1 }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            bgcolor: theme.palette.warning.light,
                            mr: 2,
                          }}
                        >
                          {getUserInitials()}
                        </Avatar>
                        <Box>
                          <Typography variant="h6">{getFullName()}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {userData?.is_admin
                              ? "Administrator"
                              : userData?.is_superuser
                                ? "Superuser"
                                : "User"}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ my: 1.5 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <EmailIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                          <Typography variant="body2">
                            {userData?.email || "No email available"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PersonIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                          <Typography variant="body2">
                            Username: {userData?.username || "N/A"}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      {userData?.sites &&
                        userData.sites.length > 0 &&
                        sites && (
                          <Box sx={{ my: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              Associated Sites:
                            </Typography>
                            <Stack
                              direction="column"
                              spacing={1}
                              flexWrap="wrap"
                            >
                              {userData.sites
                                .slice(0, 3)
                                .map((siteId, index) => {
                                  const site = sites.find(
                                    (s) => s.id === siteId
                                  );
                                  const label = site
                                    ? `${site.name} (${siteId})`
                                    : siteId;
                                  return (
                                    <Chip
                                      key={index}
                                      label={label}
                                      size="small"
                                      sx={{ fontSize: 12 }}
                                    />
                                  );
                                })}
                              {userData.sites.length > 3 && (
                                <Chip
                                  label={`+${userData.sites.length - 3} more`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                          </Box>
                        )}

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Button
                          size="small"
                          onClick={() => {
                            handleProfileClose();
                            navigate("/dashboard/account");
                          }}
                        >
                          View Profile
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            handleProfileClose();
                            handleLogoutClick();
                          }}
                        >
                          Logout
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Popover>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main content - Render child components here */}
        <Box sx={{ flexGrow: 1, p: 0, mt: 8 }}>
          <Outlet context={{ userData }} /> {/* Render nested routes */}
        </Box>

        {/* Snackbar for logout feedback */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            sx={{ width: "100%" }}
          >
            Logged out successfully!
          </Alert>
        </Snackbar>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmLogoutOpen}
          onClose={handleCancelLogout}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText
              id="alert-dialog-description"
              sx={{ color: "black" }}
            >
              Are you sure you want to log out?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelLogout} color="primary">
              No
            </Button>
            <Button onClick={handleConfirmLogout} color="error" autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Main>
    </Box>
  );
};

export default SideBar;
